'use client'

import { useState, useEffect, useCallback } from 'react'
import type { FormData } from '@/hooks/useAppStore'

const STORAGE_KEY = 'biomo_form_progress'
const STORAGE_EXPIRY_DAYS = 7 // Dados expiram em 7 dias

interface PersistedData {
  formData: Partial<FormData>
  currentStep: number
  maxStepReached: number
  savedAt: number // timestamp
  sessionId: string
}

interface UseFormPersistenceResult {
  // Dados salvos (se existirem)
  savedData: PersistedData | null
  // Se é um visitante retornando
  isReturningVisitor: boolean
  // Nome do visitante (se tiver)
  returningName: string | null
  // Step onde parou
  lastStep: number
  // Funções
  saveProgress: (formData: Partial<FormData>, currentStep: number, maxStep: number) => void
  restoreProgress: () => PersistedData | null
  clearSavedProgress: () => void
  dismissReturn: () => void
}

export function useFormPersistence(sessionId: string): UseFormPersistenceResult {
  const [savedData, setSavedData] = useState<PersistedData | null>(null)
  const [isReturningVisitor, setIsReturningVisitor] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  // Carregar dados salvos no mount
  useEffect(() => {
    if (typeof window === 'undefined') return

    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (!stored) return

      const data: PersistedData = JSON.parse(stored)

      // Verificar se expirou
      const expiryMs = STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000
      if (Date.now() - data.savedAt > expiryMs) {
        localStorage.removeItem(STORAGE_KEY)
        return
      }

      // Verificar se tem dados significativos (pelo menos nome preenchido)
      if (data.formData?.nome && data.currentStep > 0) {
        setSavedData(data)
        // É returning se é uma sessão diferente
        if (data.sessionId !== sessionId) {
          setIsReturningVisitor(true)
        }
      }
    } catch (e) {
      console.error('Erro ao carregar progresso salvo:', e)
      localStorage.removeItem(STORAGE_KEY)
    }
  }, [sessionId])

  // Salvar progresso
  const saveProgress = useCallback((
    formData: Partial<FormData>,
    currentStep: number,
    maxStep: number
  ) => {
    if (typeof window === 'undefined') return
    if (currentStep <= 0) return // Não salvar se ainda está na intro

    try {
      const data: PersistedData = {
        formData,
        currentStep,
        maxStepReached: maxStep,
        savedAt: Date.now(),
        sessionId
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
      setSavedData(data)
    } catch (e) {
      console.error('Erro ao salvar progresso:', e)
    }
  }, [sessionId])

  // Restaurar progresso
  const restoreProgress = useCallback((): PersistedData | null => {
    return savedData
  }, [savedData])

  // Limpar progresso salvo
  const clearSavedProgress = useCallback(() => {
    if (typeof window === 'undefined') return
    localStorage.removeItem(STORAGE_KEY)
    setSavedData(null)
    setIsReturningVisitor(false)
  }, [])

  // Dispensar notificação de retorno (usuário quer começar do zero)
  const dismissReturn = useCallback(() => {
    setDismissed(true)
    setIsReturningVisitor(false)
  }, [])

  return {
    savedData,
    isReturningVisitor: isReturningVisitor && !dismissed,
    returningName: savedData?.formData?.nome || null,
    lastStep: savedData?.currentStep || 0,
    saveProgress,
    restoreProgress,
    clearSavedProgress,
    dismissReturn
  }
}
